// src/components/telephony/SIPInstructions.js
// Lightweight markdown renderer for telephony instruction content.

import { useEffect, useMemo, useState } from 'react';
import sipOverview from '../../content/telephony/sip_overview.md';
import sipStepsGeneric from '../../content/telephony/sip_steps_generic.md';
import sipStepsRogers from '../../content/telephony/sip_steps_rogers.md';
import forwardingPersonal from '../../content/telephony/forwarding_personal_mobile.md';
import troubleshooting from '../../content/telephony/troubleshooting.md';

const DOCUMENT_SOURCES = {
  sip_overview: sipOverview,
  sip_steps_generic: sipStepsGeneric,
  sip_steps_rogers: sipStepsRogers,
  forwarding_personal_mobile: forwardingPersonal,
  troubleshooting
};

const inlineNodes = (text, keyPrefix) => {
  if (!text) {
    return null;
  }

  const segments = text.split(/(`[^`]+`)/g);

  return segments.map((segment, index) => {
    if (segment.startsWith('`') && segment.endsWith('`')) {
      return (
        <code key={`${keyPrefix}-code-${index}`} className="px-1 py-0.5 bg-gray-100 rounded text-sm">
          {segment.slice(1, -1)}
        </code>
      );
    }

    return <span key={`${keyPrefix}-text-${index}`}>{segment}</span>;
  });
};

const renderMarkdown = (markdown, keyPrefix) => {
  const lines = markdown.split('\n');
  const elements = [];
  let unordered = [];
  let ordered = [];

  const flushLists = () => {
    if (unordered.length > 0) {
      elements.push(
        <ul key={`${keyPrefix}-ul-${elements.length}`} className="list-disc pl-6 space-y-2 text-gray-700">
          {unordered.map((item, idx) => (
            <li key={`${keyPrefix}-ul-item-${idx}`} className="leading-6">
              {inlineNodes(item, `${keyPrefix}-ul-item-${idx}`)}
            </li>
          ))}
        </ul>
      );
      unordered = [];
    }

    if (ordered.length > 0) {
      elements.push(
        <ol key={`${keyPrefix}-ol-${elements.length}`} className="list-decimal pl-6 space-y-2 text-gray-700">
          {ordered.map((item, idx) => (
            <li key={`${keyPrefix}-ol-item-${idx}`} className="leading-6">
              {inlineNodes(item, `${keyPrefix}-ol-item-${idx}`)}
            </li>
          ))}
        </ol>
      );
      ordered = [];
    }
  };

  lines.forEach((rawLine, index) => {
    const line = rawLine.trim();

    if (!line) {
      flushLists();
      return;
    }

    if (line.startsWith('### ')) {
      flushLists();
      elements.push(
        <h3 key={`${keyPrefix}-h3-${index}`} className="text-lg font-semibold text-gray-900 mt-4">
          {line.replace('### ', '').trim()}
        </h3>
      );
      return;
    }

    if (line.startsWith('## ')) {
      flushLists();
      elements.push(
        <h2 key={`${keyPrefix}-h2-${index}`} className="text-xl font-semibold text-gray-900 mt-6">
          {line.replace('## ', '').trim()}
        </h2>
      );
      return;
    }

    if (line.startsWith('- ')) {
      unordered.push(line.slice(2));
      return;
    }

    const orderedMatch = line.match(/^(\d+)[\.\)]\s+(.*)$/);
    if (orderedMatch) {
      ordered.push(orderedMatch[2]);
      return;
    }

    flushLists();
    elements.push(
      <p key={`${keyPrefix}-p-${index}`} className="text-gray-700 leading-6">
        {inlineNodes(line, `${keyPrefix}-p-${index}`)}
      </p>
    );
  });

  flushLists();

  return elements;
};

const SIPInstructions = ({ documents = [] }) => {
  const normalizedDocs = useMemo(() => Array.from(new Set(documents)), [documents]);
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadDocuments = async () => {
      if (!normalizedDocs.length) {
        setContent([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const results = await Promise.all(
          normalizedDocs.map(async (docKey) => {
            const source = DOCUMENT_SOURCES[docKey];

            if (!source) {
              console.warn(`Unknown telephony instruction document: ${docKey}`);
              return null;
            }

            const response = await fetch(source);
            if (!response.ok) {
              throw new Error(`Failed to load ${docKey}`);
            }

            const text = await response.text();
            return {
              key: docKey,
              text
            };
          })
        );

        if (!isMounted) return;

        setContent(results.filter(Boolean));
      } catch (err) {
        console.error('Failed to fetch telephony instructions', err);
        if (isMounted) {
          setError('Unable to load instructions right now.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadDocuments();

    return () => {
      isMounted = false;
    };
  }, [normalizedDocs]);

  if (loading) {
    return <p className="text-sm text-gray-500">Loading instructions…</p>;
  }

  if (error) {
    return <p className="text-sm text-red-600">{error}</p>;
  }

  if (!content.length) {
    return <p className="text-sm text-gray-500">No instructions available for this step yet.</p>;
  }

  return (
    <div className="space-y-8">
      {content.map((doc) => (
        <article key={doc.key} className="space-y-3">
          {renderMarkdown(doc.text, doc.key)}
        </article>
      ))}
    </div>
  );
};

export default SIPInstructions;
